/**
 * Keeps the mobile PM container aligned with the keyboard-reduced viewport.
 * Viewport events only queue work because WebKit may emit another event while
 * a layout-affecting style is written.
 */
export function createMobileKeyboardViewport(pmContainer, scrollToBottom, browserWindow = window) {
  const MAX_FEEDBACK_UPDATES = 4;
  const SETTLE_DELAY_MS = 50;
  // After the burst, one trailing update applies each change. The burst and the trailing
  // write cap bound WebKit feedback (#4269). Only a focus, user activity or a window
  // resize refills them.
  const TRAILING_DELAY_MS = 200;
  const MAX_TRAILING_WRITES = 20;
  const USER_ACTIVITY_EVENTS = ['pointerdown', 'input'];
  const viewport = browserWindow.visualViewport;
  let framePending = false;
  let frameId = null;
  let scrollTimer = null;
  let settleTimer = null;
  let settling = false;
  let settledUpdateRequested = false;
  let settledFollowupsRemaining = 1;
  let updateRequested = false;
  let updatesRemaining = MAX_FEEDBACK_UPDATES;
  let isOpen = false;
  let lastHeight = null;
  let lastTop = null;
  let trailingTimer = null;
  let trailingWritesRemaining = MAX_TRAILING_WRITES;
  let trailingDropped = false;

  function applyViewport() {
    if (!isOpen || !viewport) return false;
    if (!isFixedLayout()) {
      close();
      return false;
    }

    const height = Math.round(viewport.height);
    const top = Math.round(viewport.offsetTop);
    const heightChanged = lastHeight !== height;
    const topChanged = lastTop !== top;

    if (!heightChanged && !topChanged) return false;

    lastHeight = height;
    lastTop = top;
    pmContainer.style.setProperty('height', height + 'px', 'important');
    pmContainer.style.setProperty('top', top + 'px', 'important');
    // A pan moves only `top`. A reader away from the newest message keeps their place.
    if (heightChanged) scrollToBottom(false);
    return true;
  }

  function scheduleSettledUpdate() {
    if (settleTimer !== null) browserWindow.clearTimeout(settleTimer);
    settleTimer = browserWindow.setTimeout(function() {
      settleTimer = null;
      if (!isOpen || framePending) {
        if (isOpen) scheduleSettledUpdate();
        return;
      }

      settling = true;
      settledUpdateRequested = false;
      try {
        applyViewport();
      } finally {
        settling = false;
        if (settledUpdateRequested && settledFollowupsRemaining > 0) {
          settledFollowupsRemaining -= 1;
          scheduleSettledUpdate();
        } else if (settledUpdateRequested) {
          scheduleTrailingUpdate();
        }
      }
    }, SETTLE_DELAY_MS);
  }

  function queueViewportUpdate() {
    if (!isOpen || !viewport || updatesRemaining === 0) return;

    if (framePending) {
      updateRequested = true;
      return;
    }

    framePending = true;
    updateRequested = false;
    frameId = browserWindow.requestAnimationFrame(function() {
      try {
        if (applyViewport()) updatesRemaining -= 1;
      } finally {
        const needsFollowup = updateRequested && updatesRemaining > 0;
        framePending = false;
        frameId = null;
        if (needsFollowup) queueViewportUpdate();
      }
    });
  }

  function resetUpdateBudget() {
    updatesRemaining = MAX_FEEDBACK_UPDATES;
    settledFollowupsRemaining = 1;
    settledUpdateRequested = false;
  }

  function scheduleTrailingUpdate() {
    if (trailingTimer !== null) return;
    if (trailingWritesRemaining <= 0) {
      trailingDropped = true;
      return;
    }
    trailingTimer = browserWindow.setTimeout(function() {
      trailingTimer = null;
      if (trailingWritesRemaining <= 0) {
        trailingDropped = true;
        return;
      }
      if (applyViewport()) trailingWritesRemaining -= 1;
    }, TRAILING_DELAY_MS);
  }

  function refillWriteBudget() {
    resetUpdateBudget();
    trailingWritesRemaining = MAX_TRAILING_WRITES;
    if (isOpen && trailingDropped) {
      trailingDropped = false;
      scheduleViewportUpdate();
    }
  }

  function scheduleViewportUpdate() {
    if (!isOpen || !viewport) return;
    if (settling) {
      settledUpdateRequested = true;
      return;
    }
    if (updatesRemaining === 0 && settleTimer === null) {
      if (settledFollowupsRemaining === 0) {
        scheduleTrailingUpdate();
        return;
      }
      settledFollowupsRemaining -= 1;
    }
    scheduleSettledUpdate();
    queueViewportUpdate();
  }

  // The fixed layout is a live media query, so a rotation can cross it after load.
  // A non-fixed pane ignores `top` and must keep its layout-viewport pin.
  function isFixedLayout() {
    return browserWindow.getComputedStyle(pmContainer).position === 'fixed';
  }

  function open(scrollToNewest = true) {
    if (!isFixedLayout()) return;

    isOpen = true;
    trailingDropped = false;
    refillWriteBudget();
    // close_thread() strips the inline geometry without close(), so rewrite it.
    lastHeight = null;
    lastTop = null;
    pmContainer.classList.add('keyboard-open');

    if (viewport) {
      viewport.addEventListener('resize', scheduleViewportUpdate);
      viewport.addEventListener('scroll', scheduleViewportUpdate);
      scheduleViewportUpdate();
    }
    USER_ACTIVITY_EVENTS.forEach(function(type) {
      browserWindow.addEventListener(type, refillWriteBudget, true);
    });

    if (scrollTimer !== null) browserWindow.clearTimeout(scrollTimer);
    scrollTimer = null;
    if (scrollToNewest) {
      scrollTimer = browserWindow.setTimeout(function() {
        scrollTimer = null;
        scrollToBottom(true);
      }, 300);
    }
  }

  function close() {
    const wasOpen = isOpen;
    isOpen = false;
    pmContainer.classList.remove('keyboard-open');

    if (framePending) {
      browserWindow.cancelAnimationFrame(frameId);
      framePending = false;
      frameId = null;
    }
    updateRequested = false;
    settledUpdateRequested = false;
    trailingDropped = false;
    if (settleTimer !== null) {
      browserWindow.clearTimeout(settleTimer);
      settleTimer = null;
    }
    if (scrollTimer !== null) {
      browserWindow.clearTimeout(scrollTimer);
      scrollTimer = null;
    }
    if (trailingTimer !== null) {
      browserWindow.clearTimeout(trailingTimer);
      trailingTimer = null;
    }

    if (viewport) {
      viewport.removeEventListener('resize', scheduleViewportUpdate);
      viewport.removeEventListener('scroll', scheduleViewportUpdate);
    }
    USER_ACTIVITY_EVENTS.forEach(function(type) {
      browserWindow.removeEventListener(type, refillWriteBudget, true);
    });

    pmContainer.style.removeProperty('height');
    pmContainer.style.removeProperty('top');
    lastHeight = null;
    lastTop = null;
    return wasOpen;
  }

  function syncLayout(inputFocused) {
    const fixed = isFixedLayout();
    if (isOpen && !fixed) {
      close();
    } else if (isOpen) {
      refillWriteBudget();
      scheduleViewportUpdate();
    } else if (fixed && inputFocused) {
      // A rotation or a narrowed window is not a focus. A reader in history keeps their place.
      open(false);
    }
  }

  return { open, close, syncLayout };
}
